import { capitalize } from 'lodash'
import {
  InsiConnectInsRequest,
  InsiConnectInsResponse,
  InsiIdentityCheckPayload,
  InsiPatient,
  InsiPatientErrorFields,
} from '../model/Insi'
import { IdentityState, Patient,IdentityStatus,Sex } from '../model/Patient'
import { formatFr, parseApiFormat } from './date.utilities'
import { isDefined } from './functions.utilities'
import { isValid } from 'date-fns'
import { PatientFormFields } from '../components/patient/PatientForm/PatientForm.model'
import { MailPatient } from '../model/Mail'
import { envBehavior } from './environment.utilities'
import { deserializeInsiPayload } from '../components/patient/PatientForm/IdentityStatusBlock/IdentityStatusBlock.utils'

import type { IdentityCheckStatus } from '@follow/cdk/src/model/types/Patient.types'
import { TrustedSource} from '@follow/cdk'


interface IdentityStatusUpdate {
  state: IdentityState
  insiStatus: IdentityCheckStatus
  showTrustedSource: boolean
  trustedSource: TrustedSource | null
}
export const insiSexMapper = (insiSex: 'F' | 'M') => {
  switch (insiSex) {
    case 'M':
      return Sex.MALE
    case 'F':
      return Sex.FEMALE
    default:
      return Sex.UNKNOWN
  }
}

export const insiConsultPatientMapper = (response: InsiConnectInsResponse): InsiPatient |  null => {
  if (!response.error && response.Ins) {
    const { sex, birthDate, birthPlace } = response
    const { nir, oid, key } = response.Ins
    const lastname: string = response.name
    const firstnameList: string = response.birthGiven
    const [firstname, ...otherFirstnames] = firstnameList.split(' ').map(capitalize)
    const patchedOid = mapAssigninAuthoritiesToTestEquivalent(oid)

    return {
      birthFirstName: firstname,
      birthLastName: lastname.toUpperCase(),
      usedFirstName: null,
      otherFirstName: [firstname, ...otherFirstnames].join(' '),
      sex: insiSexMapper(sex),
      birthDate: birthDate,
      birthPlaceCode: birthPlace,
      assigningAuthority: { label: '', oid: patchedOid },
      inseeNumber: nir + key,
      codeCR: response.CR?.codeCR
    }
  } else {
    return null;
  }
}

export const insiPatientSanityCheck = ({
  birthDate,
  birthFirstName,
  birthLastName,
  sex,
}: InsiPatient): InsiPatientErrorFields[] => {
  let errors: InsiPatientErrorFields[] = []
  if (!isDefined(birthDate) || !isValid(new Date(birthDate))) {
    errors.push('birthDate')
  }
  if (!isDefined(birthFirstName) || birthFirstName === '') {
    errors.push('birthFirstName')
  }
  if (!isDefined(birthLastName) || birthLastName === '') {
    errors.push('birthLastName')
  }
  if (!isDefined(sex) || sex === Sex.UNKNOWN) {
    errors.push('sex')
  }

  return errors
}

export const patientToInsiRestPatientPayload = (
  patient: MailPatient | PatientFormFields,
): InsiIdentityCheckPayload | null => {
  const { birthFirstName, birthLastName, sex, birthDate } = patient

  if (isDefined(sex))
    return {
      birthFirstName,
      birthLastName,
      birthDate,
      sex,
    }
  return null
}

export const insiConsultSerializer = (patient: InsiIdentityCheckPayload): InsiConnectInsRequest => {
  return {
    name: patient.birthLastName,
    given: patient.birthFirstName,
    birthday: formatFr(parseApiFormat(patient.birthDate), 'dd/MM/yyyy'),
    sex: patient.sex === Sex.MALE ? '2' : '3',
  }
}

export const determineIdentityStatus = (
  failedCheck: boolean,
  fromTrustedSource: boolean,
  currentStatus?: {
    showTrustedSource?: boolean
    trustedSource?: TrustedSource | null
  },
): IdentityStatusUpdate => {
  const baseStatus: IdentityStatusUpdate = {
    state: failedCheck ? IdentityState.TEMPORARY : IdentityState.VALIDATED,
    insiStatus: failedCheck ? 'FAILED' : 'VALIDATED',
    showTrustedSource: currentStatus?.showTrustedSource ?? false,
    trustedSource: currentStatus?.trustedSource ?? null
  }

  if (fromTrustedSource && !failedCheck) {
    return {
      ...baseStatus,
      state: IdentityState.QUALIFIED,
      showTrustedSource: true,
      trustedSource:
        currentStatus?.trustedSource ?? ("Carte d'identité européenne" as unknown as TrustedSource),
    }
  }

  return baseStatus
}

// Selon les exigences Segur1, un patient dont la validation INSi a échoué doit être
// créé sans matricule INS et sans OID
export const getNewPatientFromMail = (
  patient: Partial<Patient>,
  failedCheck: boolean,
  insiPatient?: InsiPatient,
  fromTrustedSource?: boolean,
): Partial<Patient> => {


  const deserializedInsiPatient = insiPatient ? deserializeInsiPayload(insiPatient) : {}
  const mergedPatient = {
    ...deserializedInsiPatient,
    ...patient,
  }
  const identityStatus = determineIdentityStatus(
    failedCheck,
    fromTrustedSource || false,
    patient.identityStatus,
  )
  return {
    ...mergedPatient,
    inseeNumber: failedCheck ? null : mergedPatient.inseeNumber,
    assigningAuthority: failedCheck ? null : mergedPatient.assigningAuthority,
    identityStatus: {
      id: null,
      suspicious: false,
      fictitious: false,
      showTrustedSource: identityStatus.showTrustedSource ?? mergedPatient.identityStatus?.showTrustedSource ?? false,
      trustedSource: identityStatus?.trustedSource ?? mergedPatient.identityStatus?.trustedSource ?? null,
      state: identityStatus.state,
      insiStatus: {
        checked: identityStatus.insiStatus,
        checkedAt: new Date(),
      },
    } as IdentityStatus
  }
}

export const getExistingPatientFromMail = (patient: Partial<Patient>): Partial<Patient> => {
  return {
    ...patient,
    identityStatus: {
      ...patient.identityStatus,
      state: IdentityState.VALIDATED,
      insiStatus: {
        checkedAt: new Date(),
        checked: 'VALIDATED',
      },
    } as IdentityStatus,
  }
}

// Monkey patch des oid pour les environnements de test pour utiliser les OID de test sur les patients de test
export const mapAssigninAuthoritiesToTestEquivalent = (oid: string) => {
  if (envBehavior !== 'test') return oid

  return oid.replace(/4\.8$/g, '4.10').replace(/4\.2$/g, '4.6')
}
